import i18n from '@/i18n';

import { normalizePlatformCode } from '@/utils/platformShopCatalog';
import { parseBatchTokens } from '@/shein-platform/modules/products/components/batchQueryUtils';
import {
  normalizeVideoAspectRatio,
  normalizeVideoDurationSec,
  normalizeVideoGenerationMode,
  normalizeVideoSeed,
  VIDEO_TEMU_DEFAULT_ASPECT_RATIO,
  VIDEO_DEFAULT_DURATION_SEC,
  VIDEO_DEFAULT_GENERATION_MODE,
  VIDEO_DEFAULT_SEED,
  VIDEO_I2V_SUPPLEMENT_MAX_LENGTH,
} from '@/pages/ipe/workbench/constants/videoStudioConfig';
import { isVideoToolsConfigLocked } from '@/pages/ipe/workbench/constants/videoToolsMaintenance';
import {
  AI_COPYWRITING_LANGUAGE_OPTIONS,
  DEFAULT_AI_COPYWRITING_LANGUAGE,
  normalizeCopywritingMaxChars,
  sanitizeCopywritingMaxCharsInput,
} from '@/pages/ipe/workbench/constants/aiCopywritingConfig';

const AI_COPYWRITING_LANGUAGE_CODES = new Set(
  AI_COPYWRITING_LANGUAGE_OPTIONS.map((item) => item.value),
);

function normalizeAiCopywritingLanguage(raw) {
  const code = String(raw || '').trim();
  if (!code) return DEFAULT_AI_COPYWRITING_LANGUAGE;
  if (AI_COPYWRITING_LANGUAGE_CODES.has(code)) return code;
  // 兼容旧规则自由文本：zh / en 等
  const lower = code.toLowerCase();
  if (lower === 'zh' || lower.startsWith('zh-cn') || lower.includes('简')) return 'zh-CN';
  if (lower.startsWith('zh-tw') || lower.includes('繁')) return 'zh-TW';
  if (lower === 'en' || lower.startsWith('en-')) return 'en';
  if (lower === 'de' || lower.startsWith('de-')) return 'de';
  if (lower === 'fr' || lower.startsWith('fr-')) return 'fr';
  if (lower === 'ja' || lower.startsWith('ja-')) return 'ja';
  if (lower === 'ko' || lower.startsWith('ko-')) return 'ko';
  return DEFAULT_AI_COPYWRITING_LANGUAGE;
}

const RISK_LEVELS = ['high', 'medium', 'low'];

/** 自动规则文本筛选：逗号/换行批量；任一 token 命中即可（包含匹配，忽略大小写）。 */
export function matchListingAutoRuleText(haystack, keywordOrTokens) {
  const tokens = Array.isArray(keywordOrTokens)
    ? keywordOrTokens.map((t) => String(t || '').trim().toLowerCase()).filter(Boolean)
    : parseBatchTokens(keywordOrTokens).map((t) => t.toLowerCase());
  if (!tokens.length) return true;
  const hay = String(haystack || '').trim().toLowerCase();
  if (!hay) return false;
  return tokens.some((t) => hay.includes(t));
}

export function normalizeListingSkipRiskLevels(raw) {
  const out = [];
  const seen = new Set();
  const list = Array.isArray(raw)
    ? raw
    : (typeof raw === 'string' && raw.trim()
      ? raw.split(/[,\s]+/)
      : []);
  list.forEach((item) => {
    const level = String(item || '').trim().toLowerCase();
    if (!RISK_LEVELS.includes(level) || seen.has(level)) return;
    seen.add(level);
    out.push(level);
  });
  return out;
}

/** 商品库自动规则步骤：AI 文案 → 图生视频 → 侵权检测 → 自动上架。关闭则跳过。 */

export const LISTING_AUTO_RULE_STEPS = ['aiCopywriting', 'videoGeneration', 'infringement', 'listing'];

export const LISTING_AUTO_RULE_STEP_META = {
  aiCopywriting: {
    key: 'aiCopywriting',
    enabledKey: 'runAiCopywriting',
    title: 'AI 文案',
    hint: '生成标题；与视频生成、侵权检测并行；关闭则跳过',
  },
  videoGeneration: {
    key: 'videoGeneration',
    enabledKey: 'runVideoGeneration',
    title: '视频生成',
    hint: '图生视频：默认将同一 SPU 下全部主图传给模型；与 AI 文案、侵权检测并行；关闭则跳过',
  },
  infringement: {
    key: 'infringement',
    enabledKey: 'runInfringement',
    title: '侵权检测',
    hint: '对主图发起检测；与 AI 文案、视频生成并行；关闭则跳过',
  },
  listing: {
    key: 'listing',
    enabledKey: 'runListing',
    title: '自动上架',
    hint: '预处理全部完成后按店铺上架；受平台 QPS 约束，多店抢占额度；可选手动立即/定时，并可叠加间隔上架',
  },
};

export const LISTING_MODE_IMMEDIATE = 'immediate';
export const LISTING_MODE_SCHEDULED = 'scheduled';

/** 间隔上架：每个 SPU 之间的分钟数（1～1440） */
export const LISTING_INTERVAL_MIN_MINUTES = 1;
export const LISTING_INTERVAL_MAX_MINUTES = 1440;
export const LISTING_INTERVAL_DEFAULT_MINUTES = 1;

export function normalizeListingMode(raw) {
  const mode = String(raw || '').trim().toLowerCase();
  return mode === LISTING_MODE_SCHEDULED ? LISTING_MODE_SCHEDULED : LISTING_MODE_IMMEDIATE;
}

export function normalizeListingIntervalMinutes(raw, { enabled = false } = {}) {
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return enabled ? LISTING_INTERVAL_DEFAULT_MINUTES : null;
  }
  const rounded = Math.round(n);
  if (rounded < LISTING_INTERVAL_MIN_MINUTES || rounded > LISTING_INTERVAL_MAX_MINUTES) {
    return enabled ? LISTING_INTERVAL_DEFAULT_MINUTES : null;
  }
  return rounded;
}

const LEGACY_STEP_ALIASES = {
  ai: 'aiCopywriting',
  ai_copywriting: 'aiCopywriting',
  copywriting: 'aiCopywriting',
  video: 'videoGeneration',
  video_generation: 'videoGeneration',
  i2v: 'videoGeneration',
  image_to_video: 'videoGeneration',
  infringe: 'infringement',
  infringement_check: 'infringement',
  download: null,
  zip: null,
  export: null,
  export_zip: null,
  template: null,
  template_export: null,
  listingexport: null,
  templateexport: null,
  listing_template: null,
};

export function normalizeListingAutoRuleSteps(raw) {
  const allowed = new Set(LISTING_AUTO_RULE_STEPS);
  const seen = new Set();
  const present = [];
  (Array.isArray(raw) ? raw : []).forEach((item) => {
    const key = normalizeListingAutoRuleStepKey(item);
    if (!key || !allowed.has(key) || seen.has(key)) return;
    seen.add(key);
    present.push(key);
  });
  if (present.length === 0) {
    return [...LISTING_AUTO_RULE_STEPS];
  }
  const out = [...present];
  LISTING_AUTO_RULE_STEPS.forEach((key, defaultIdx) => {
    if (seen.has(key)) return;
    const insertAt = Math.min(Math.max(defaultIdx, 0), out.length);
    out.splice(insertAt, 0, key);
    seen.add(key);
  });
  return out;
}

export function normalizeListingAutoRuleStepKey(raw) {
  const key = String(raw || '').trim();
  if (!key) return null;
  if (LISTING_AUTO_RULE_STEPS.includes(key)) return key;
  const aliased = LEGACY_STEP_ALIASES[key.toLowerCase()];
  if (aliased === null) return null;
  return aliased || null;
}

export function normalizeListingAutoRule(rule) {
  if (!rule || typeof rule !== 'object') return null;
  const pipelineSteps = normalizeListingAutoRuleSteps(rule.pipelineSteps);
  let selectedStep = pipelineSteps.includes(rule.selectedStep)
    ? rule.selectedStep
    : pipelineSteps[0];
  // 视频工具维护中：禁止开启/停留在视频步骤
  const videoLocked = isVideoToolsConfigLocked();
  if (videoLocked && selectedStep === 'videoGeneration') {
    selectedStep = pipelineSteps.find((key) => key !== 'videoGeneration') || selectedStep;
  }
  const shopId = rule.shopId != null && String(rule.shopId).trim() !== ''
    ? String(rule.shopId)
    : '';
  return {
    ...rule,
    id: rule.id != null ? rule.id : null,
    name: String(rule.name || '').trim() || '默认规则',
    enabled: !!rule.enabled,
    runAiCopywriting: !!rule.runAiCopywriting,
    runVideoGeneration: videoLocked ? false : !!rule.runVideoGeneration,
    runInfringement: !!rule.runInfringement,
    runListing: !!rule.runListing,
    pipelineSteps,
    selectedStep,
    aiLanguage: normalizeAiCopywritingLanguage(rule.aiLanguage),
    aiPrompt: String(rule.aiPrompt || ''),
    aiOutputCount: Number(rule.aiOutputCount) > 0 ? Number(rule.aiOutputCount) : 3,
    aiMaxChars: sanitizeCopywritingMaxCharsInput(
      rule.aiMaxChars != null ? rule.aiMaxChars : rule.ai_max_chars,
    ),
    aiTitlePresetId: String(rule.aiTitlePresetId || rule.ai_title_preset_id || '').trim(),
    videoAspectRatio: normalizeVideoAspectRatio(rule.videoAspectRatio || VIDEO_TEMU_DEFAULT_ASPECT_RATIO),
    videoDurationSec: normalizeVideoDurationSec(
      rule.videoDurationSec != null ? rule.videoDurationSec : VIDEO_DEFAULT_DURATION_SEC,
    ),
    videoGenerationMode: normalizeVideoGenerationMode(
      rule.videoGenerationMode || VIDEO_DEFAULT_GENERATION_MODE,
    ),
    videoSeed: normalizeVideoSeed(rule.videoSeed != null ? rule.videoSeed : VIDEO_DEFAULT_SEED),
    videoSupplementPrompt: String(rule.videoSupplementPrompt || '')
      .slice(0, VIDEO_I2V_SUPPLEMENT_MAX_LENGTH),
    videoModelImageUrl: String(rule.videoModelImageUrl || rule.video_model_image_url || '').trim(),
    infringementMode: rule.infringementMode === 'deep' ? 'deep' : 'basic',
    listingSkipRiskLevels: normalizeListingSkipRiskLevels(rule.listingSkipRiskLevels),
    listingMode: normalizeListingMode(rule.listingMode),
    listingScheduledAt: String(rule.listingScheduledAt || '').trim() || '',
    listingIntervalEnabled: !!rule.listingIntervalEnabled,
    listingIntervalMinutes: normalizeListingIntervalMinutes(rule.listingIntervalMinutes, {
      enabled: !!rule.listingIntervalEnabled,
    }) ?? LISTING_INTERVAL_DEFAULT_MINUTES,
    shopId,
    shopType: rule.shopType || '',
    shopName: rule.shopName || '',
    platformCode: normalizePlatformCode(rule.platformCode) || '',
    categoryId: rule.categoryId || '',
    productTypeId: rule.productTypeId || '',
    categoryPathLabel: rule.categoryPathLabel || '',
    productTemplateId: rule.productTemplateId != null ? String(rule.productTemplateId) : '',
    productTemplateName: rule.productTemplateName || '',
    templateScope: rule.templateScope || '',
  };
}

export function normalizeListingAutoRuleList(payload) {
  const items = Array.isArray(payload?.items)
    ? payload.items
    : (Array.isArray(payload) ? payload : []);
  return items.map((item) => normalizeListingAutoRule(item)).filter(Boolean);
}

/**
 * 新建弹窗用的本地草稿（未落库）。默认开启「商品入库后自动上架」与「自动上架」步骤。
 */
export function createListingAutoRuleDraft({ name, ...overrides } = {}) {
  return normalizeListingAutoRule({
    id: null,
    name: String(name || '').trim() || '默认规则',
    enabled: true,
    runAiCopywriting: false,
    runVideoGeneration: false,
    runInfringement: false,
    runListing: true,
    selectedStep: 'listing',
    ...overrides,
  });
}

/** 启用中的规则（带入本地商品库时可选） */
export function listEnabledListingAutoRules(rules = []) {
  return (Array.isArray(rules) ? rules : []).filter((rule) => !!rule?.enabled && rule?.id != null);
}

export function toListingAutoRulePayload(rule) {
  const normalized = normalizeListingAutoRule(rule);
  if (!normalized) return null;
  const { selectedStep, ...rest } = normalized;
  return {
    id: rest.id ?? undefined,
    name: rest.name || '默认规则',
    enabled: !!rest.enabled,
    runAiCopywriting: !!rest.runAiCopywriting,
    runVideoGeneration: !!rest.runVideoGeneration,
    runInfringement: !!rest.runInfringement,
    runListing: !!rest.runListing,
    pipelineSteps: rest.pipelineSteps,
    aiLanguage: rest.aiLanguage || DEFAULT_AI_COPYWRITING_LANGUAGE,
    aiPrompt: rest.aiPrompt || null,
    aiOutputCount: rest.aiOutputCount,
    aiMaxChars: normalizeCopywritingMaxChars(rest.aiMaxChars) ?? null,
    aiTitlePresetId: rest.aiTitlePresetId || null,
    videoAspectRatio: rest.videoAspectRatio || VIDEO_TEMU_DEFAULT_ASPECT_RATIO,
    videoDurationSec: rest.videoDurationSec,
    videoGenerationMode: rest.videoGenerationMode || VIDEO_DEFAULT_GENERATION_MODE,
    videoSeed: rest.videoSeed,
    videoSupplementPrompt: rest.videoSupplementPrompt || null,
    videoModelImageUrl: rest.videoModelImageUrl || null,
    infringementMode: rest.infringementMode,
    listingSkipRiskLevels: normalizeListingSkipRiskLevels(rest.listingSkipRiskLevels),
    listingMode: normalizeListingMode(rest.listingMode),
    listingScheduledAt: normalizeListingMode(rest.listingMode) === LISTING_MODE_SCHEDULED
      ? (String(rest.listingScheduledAt || '').trim() || null)
      : null,
    listingIntervalEnabled: !!rest.listingIntervalEnabled,
    listingIntervalMinutes: rest.listingIntervalEnabled
      ? (normalizeListingIntervalMinutes(rest.listingIntervalMinutes, { enabled: true })
        ?? LISTING_INTERVAL_DEFAULT_MINUTES)
      : null,
    shopId: rest.shopId ? Number(rest.shopId) || rest.shopId : null,
    shopType: rest.shopType || null,
    shopName: rest.shopName || null,
    categoryId: rest.categoryId || null,
    productTypeId: rest.productTypeId || null,
    categoryPathLabel: rest.categoryPathLabel || null,
    productTemplateId: rest.productTemplateId || null,
    productTemplateName: rest.productTemplateName || null,
    templateScope: rest.templateScope || null,
  };
}

/** @returns {string} 空字符串表示通过 */
export function validateListingAutoRuleSave(rule) {
  const normalized = normalizeListingAutoRule(rule);
  if (!normalized) return i18n.t('workbench:listingAutoRule.validateBadRule');
  if (!normalized.runListing) return '';

  if (!String(normalized.shopType || '').trim()) return i18n.t('workbench:listingAutoRule.validateShopType');
  if (!String(normalized.shopId || '').trim()) return i18n.t('workbench:listingAutoRule.validateShop');
  if (!String(normalized.categoryId || '').trim()) return i18n.t('workbench:listingAutoRule.validateCategory');
  if (!String(normalized.productTemplateId || '').trim()) return i18n.t('workbench:listingAutoRule.validateTemplate');

  if (normalizeListingMode(normalized.listingMode) === LISTING_MODE_SCHEDULED) {
    const at = String(normalized.listingScheduledAt || '').trim();
    if (!at) return i18n.t('workbench:listingAutoRule.validateScheduledAt');
    const ms = Date.parse(at);
    if (!Number.isFinite(ms)) return i18n.t('workbench:listingAutoRule.validateScheduledFormat');
    if (ms <= Date.now()) return i18n.t('workbench:listingAutoRule.validateScheduledFuture');
  }
  if (normalized.listingIntervalEnabled) {
    const minutes = normalizeListingIntervalMinutes(normalized.listingIntervalMinutes, { enabled: true });
    if (minutes == null) {
      return i18n.t('workbench:listingAutoRule.validateInterval', { min: LISTING_INTERVAL_MIN_MINUTES, max: LISTING_INTERVAL_MAX_MINUTES });
    }
  }
  return '';
}

export function summarizeListingAutoRule(rule) {
  const normalized = normalizeListingAutoRule(rule);
  if (!normalized) return '';
  const parts = [];
  if (normalized.shopName || normalized.shopId) {
    parts.push(normalized.shopName || `店铺#${normalized.shopId}`);
  }
  if (normalized.categoryPathLabel || normalized.categoryId) {
    parts.push(normalized.categoryPathLabel || `类目 ${normalized.categoryId}`);
  }
  if (normalized.productTemplateName || normalized.productTemplateId) {
    parts.push(normalized.productTemplateName || `模板#${normalized.productTemplateId}`);
  }
  return parts.join(' · ');
}

/**
 * 解析批次/任务手动「商品刊登」应携带的自动规则 id（后端判断是否定时入队）。
 * 优先批次/任务绑定，其次当前面板选中的规则。
 */
export function resolveListingAutoRuleIdForPublish({
  boundRuleId = null,
  autoRule = null,
} = {}) {
  const bound = String(boundRuleId || '').trim();
  if (bound) return bound;
  const selected = String(autoRule?.id || '').trim();
  return selected || null;
}

/**
 * 解析当前启用且会进入定时库的自动规则 id（定时上架，或自动/定时 + 间隔上架）。
 */
export function resolveScheduledListingAutoRuleId({
  boundRuleId = null,
  autoRule = null,
  autoRules = [],
} = {}) {
  const candidates = [];
  const pushCandidate = (id) => {
    const key = String(id || '').trim();
    if (!key || candidates.includes(key)) return;
    candidates.push(key);
  };
  pushCandidate(boundRuleId);
  pushCandidate(autoRule?.id);

  for (const id of candidates) {
    const rule = String(autoRule?.id) === String(id)
      ? normalizeListingAutoRule(autoRule)
      : normalizeListingAutoRule((autoRules || []).find((item) => String(item?.id) === String(id)));
    if (!rule) continue;
    if (!isDeferredListingAutoRule(rule)) continue;
    return rule.id;
  }
  return null;
}

export function isScheduledListingAutoRule(rule) {
  const normalized = normalizeListingAutoRule(rule);
  if (!normalized?.enabled || !normalized.runListing) return false;
  return normalizeListingMode(normalized.listingMode) === LISTING_MODE_SCHEDULED
    && Boolean(String(normalized.listingScheduledAt || '').trim());
}

/** 定时上架，或开启间隔上架（与立即/定时兼容）时，预检后进入定时库。 */
export function isDeferredListingAutoRule(rule) {
  const normalized = normalizeListingAutoRule(rule);
  if (!normalized?.enabled || !normalized.runListing) return false;
  if (normalized.listingIntervalEnabled) {
    const minutes = normalizeListingIntervalMinutes(normalized.listingIntervalMinutes, { enabled: true });
    if (minutes != null) return true;
  }
  return isScheduledListingAutoRule(normalized);
}
